import { useMemo, useRef } from "react";
import { Loader2 } from "lucide-react";
import { SafeMarkdown } from "@/components/markdown/SafeMarkdown";
import remarkGfm from "remark-gfm";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { citationHref, isWebUrl } from "@/lib/citationMarkers";
import type { AnswerReasoning, AnswerReasoningClaim } from "@/types/chat";
import {
  buildCitationDisplayNumbers,
  citationEntryKey,
  type CitationEntry,
} from "./citationUtils";

export const ANSWER_REASONING_LABEL = "Answer Reasoning";

interface AnswerReasoningPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: number;
  citations?: CitationEntry[];
  contentForOrdering?: string;
  answerReasoning: AnswerReasoning | null;
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
}

function citationKeyForIndex(
  citations: CitationEntry[] | undefined,
  citationIndex: number,
): string {
  const entry = citations?.[citationIndex - 1];
  if (!entry) return String(citationIndex);
  return citationEntryKey(entry) || String(citationIndex);
}

function displayNumberForStoredIndex(
  displayNumbers: Map<string, number>,
  citations: CitationEntry[] | undefined,
  citationIndex: number,
): number {
  const key = citationKeyForIndex(citations, citationIndex);
  return (
    displayNumbers.get(key) ??
    displayNumbers.get(String(citationIndex)) ??
    citationIndex
  );
}

function openCitation(
  messageId: number,
  key: string,
  contentForOrdering?: string,
  originalIndex?: number,
): void {
  if (typeof window === "undefined" || (!key && originalIndex == null)) return;
  window.dispatchEvent(
    new CustomEvent("citation:open", {
      detail: {
        messageId,
        key,
        ...(originalIndex != null ? { originalIndex } : {}),
        ...(contentForOrdering ? { contentForOrdering } : {}),
      },
    }),
  );
}

/** Excerpt-scoped allowlist; return undefined so blocked schemes stay inert. */
function excerptUrlTransform(href?: string): string | undefined {
  if (!href) return undefined;
  if (
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:")
  ) {
    return href;
  }
  return undefined;
}

function SourceHeading({
  displayNumber,
  sourceLabel,
  onOpenCitation,
}: {
  displayNumber: number;
  sourceLabel: string;
  onOpenCitation: () => void;
}) {
  const webUrl = isWebUrl(sourceLabel);

  return (
    <div className="mb-1 flex items-start gap-1.5 text-xs font-semibold">
      <button
        type="button"
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 dark:border-blue-800/50 dark:bg-blue-900/30 dark:text-blue-300"
        aria-label={`Open citation ${displayNumber}`}
        onClick={onOpenCitation}
      >
        {displayNumber}
      </button>
      {webUrl ? (
        <a
          href={citationHref(sourceLabel)}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all text-blue-600 underline hover:text-blue-800"
        >
          {sourceLabel}
        </a>
      ) : (
        <button
          type="button"
          className="text-left text-muted-foreground hover:text-foreground"
          aria-label={`Open citation ${displayNumber}: ${sourceLabel}`}
          onClick={onOpenCitation}
        >
          {sourceLabel}
        </button>
      )}
    </div>
  );
}

function hasNonEmptyExcerptText(excerpt: string): boolean {
  return excerpt.trim().length > 0;
}

function supportedClaims(claims: AnswerReasoningClaim[]): AnswerReasoningClaim[] {
  return claims
    .filter((claim) => !claim.unsupported)
    .map((claim) => ({
      ...claim,
      excerpts: claim.excerpts.filter((excerpt) => hasNonEmptyExcerptText(excerpt.excerpt)),
    }))
    .filter((claim) => claim.excerpts.length > 0);
}

function ClaimBlock({
  claim,
  displayNumberForIndex,
  onExcerptClick,
}: {
  claim: AnswerReasoningClaim;
  displayNumberForIndex: (citationIndex: number) => number;
  onExcerptClick: (citationIndex: number) => void;
}) {
  return (
    <div className="border-b border-border pb-4 last:border-b-0 last:pb-0">
      <p className="text-sm font-medium text-foreground">{claim.text}</p>
      <div className="mt-2 space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Based on these sources
        </p>
        {claim.excerpts.map((excerpt, idx) => (
          <div
            key={`${excerpt.citation_index}-${idx}`}
            className="rounded-md border border-border bg-muted/40 p-3"
          >
            <SourceHeading
              displayNumber={displayNumberForIndex(excerpt.citation_index)}
              sourceLabel={excerpt.source_label}
              onOpenCitation={() => onExcerptClick(excerpt.citation_index)}
            />
            <div className="prose prose-sm dark:prose-invert max-w-none text-sm text-foreground prose-p:my-1 prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800">
              <SafeMarkdown
                remarkPlugins={[remarkGfm]}
                urlTransform={excerptUrlTransform}
                components={{
                  a: ({ href, children }) => {
                    if (!href) {
                      return <>{children}</>;
                    }
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {excerpt.excerpt}
              </SafeMarkdown>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AnswerReasoningPanel({
  open,
  onOpenChange,
  messageId,
  citations,
  contentForOrdering,
  answerReasoning,
  isLoading,
  error,
  onRetry,
}: AnswerReasoningPanelProps) {
  const pendingCitationIndexRef = useRef<number | null>(null);

  const handleExcerptClick = (citationIndex: number) => {
    pendingCitationIndexRef.current = citationIndex;
    onOpenChange(false);
  };

  const handleCloseAutoFocus = (event: Event) => {
    const citationIndex = pendingCitationIndexRef.current;
    pendingCitationIndexRef.current = null;
    if (citationIndex == null) return;
    // Only steal focus restore when opening Citations; normal dismiss keeps Radix behavior.
    event.preventDefault();
    openCitation(
      messageId,
      citationKeyForIndex(citations, citationIndex),
      contentForOrdering,
      citationIndex,
    );
  };

  const citationDisplayNumbers = useMemo(
    () => buildCitationDisplayNumbers(citations, contentForOrdering ?? ""),
    [citations, contentForOrdering],
  );

  const resolveDisplayNumber = (citationIndex: number) =>
    displayNumberForStoredIndex(citationDisplayNumbers, citations, citationIndex);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-lg"
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        <SheetHeader className="shrink-0 border-b bg-background px-6 pb-4 pt-6 pr-12 text-left">
          <SheetTitle>{ANSWER_REASONING_LABEL}</SheetTitle>
          <SheetDescription className="sr-only">
            Claim-by-claim mapping from this answer to cited source excerpts.
          </SheetDescription>
        </SheetHeader>
        <div
          data-testid="answer-reasoning-scroll"
          className="ordinal-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4"
        >
          {isLoading && (
            <div
              className="flex items-center gap-3"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <Loader2
                className="h-5 w-5 shrink-0 animate-spin text-muted-foreground"
                aria-hidden
              />
              <p className="min-w-0 text-sm font-semibold text-foreground">
                Generating {ANSWER_REASONING_LABEL}
              </p>
            </div>
          )}
          {!isLoading && error && (
            <div className="space-y-3" role="alert">
              <p className="text-sm text-destructive">{error}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="text-sm font-medium text-foreground underline underline-offset-2 hover:text-muted-foreground"
                >
                  Try again
                </button>
              )}
            </div>
          )}
          {!isLoading && !error && answerReasoning && (
            <div className="space-y-4">
              {(() => {
                const claims = supportedClaims(answerReasoning.claims);
                if (claims.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground">No claims were returned for this answer.</p>
                  );
                }
                return claims.map((claim, index) => (
                  <ClaimBlock
                    key={`${claim.text}-${index}`}
                    claim={claim}
                    displayNumberForIndex={resolveDisplayNumber}
                    onExcerptClick={handleExcerptClick}
                  />
                ));
              })()}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
